import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { PrivatePresentationCTA } from "@/components/PrivatePresentationCTA";
import { useLang, local } from "@/i18n/LanguageContext";

// Homepage — compression stratégique du parcours.
// 5 sections, dans l'ordre validé :
//   1. Hero
//   2. Preuve matière
//   3. Pièces choisies
//   4. Deux voies (Les Pièces / Études Privées)
//   5. PrivatePresentationCTA (composant partagé, inchangé)
const featuredIdx = [0, 2, 4]; // Rosa, Lumumba, Quincy — conservé

const Home = () => {
  const { lang, t } = useLang();
  const home = t.home;
  const featured = featuredIdx.map((i) => PRODUCTS[i]);

  return (
    <div data-testid="home-page">
      {/* HERO ----------------------------------------------------------- */}
      <section className="relative overflow-hidden">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16 pt-8 md:pt-12 lg:pt-16 pb-20 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-20 items-start">

            {/* 1. Identité — eyebrow + H1 + tagline
                Mobile : arrive en tête pour que l'identité soit lisible
                immédiatement, avant l'image. Desktop : moitié gauche, 1re rangée. */}
            <div className="lg:col-span-6 lg:col-start-1 lg:row-start-1 reveal">
              <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-6 lg:mb-10">
                Paris
              </p>
              <h1 className="display-hero text-[hsl(var(--md-ink))]" data-testid="hero-h1">
                {home.title}
              </h1>
              <p className="font-display text-[1.5rem] md:text-[1.85rem] mt-6 lg:mt-8 text-[hsl(var(--md-ink))] max-w-[28ch] leading-tight">
                {home.tagline}
              </p>
            </div>

            {/* 2. Image Daisuki
                Mobile : sous l'identité, ratio 4/5.
                Desktop : colonne droite, hauteur des deux rangées (row-span-2). */}
            <div className="lg:col-span-6 lg:col-start-7 lg:row-start-1 lg:row-span-2 reveal reveal-d2">
              <div className="relative aspect-[4/5] lg:aspect-[3/4] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))]">
                <img
                  src={PRODUCTS[1].image}
                  alt="Daisuki — Maison Dodji"
                  className="absolute inset-0 w-full h-full object-cover"
                  width="1150"
                  height="1150"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 flex items-end justify-between bg-gradient-to-t from-[hsl(var(--md-bone))]/90 to-transparent pt-24">
                  <div>
                    <p className="font-eyebrow text-[hsl(var(--md-stone))]">{home.featureEyebrow}</p>
                    <p className="font-display text-[1.6rem] text-[hsl(var(--md-ink))] mt-1">Daisuki</p>
                  </div>
                  <Link
                    to="/collection/daisuki"
                    className="font-eyebrow text-[hsl(var(--md-ink))] flex items-center gap-2 hover:gap-3 transition-all"
                    data-testid="hero-feature-daisuki"
                  >
                    {home.featureCta} <ArrowRight size={14} strokeWidth={1.2} />
                  </Link>
                </div>
              </div>
            </div>

            {/* 3. Proposition + actions
                Mobile : après l'image, prolonge la lecture.
                Desktop : moitié gauche, 2e rangée sous l'identité. */}
            <div className="lg:col-span-6 lg:col-start-1 lg:row-start-2 reveal">
              <p className="editorial-muted max-w-[52ch]" data-testid="hero-description">
                {home.heroDescription}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-5 sm:gap-10 items-start">
                <Link to="/collection" className="btn-primary" data-testid="hero-cta-pieces">
                  {home.heroCtaPieces}
                </Link>
                <Link to="/contact" className="btn-ghost" data-testid="hero-cta-etudes">
                  {home.heroCtaEtudes}
                  <ArrowUpRight size={14} strokeWidth={1.25} />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* PREUVE MATIÈRE ------------------------------------------------- */}
      <section
        className="border-t border-[hsl(var(--md-line))] py-16 md:py-20"
        data-testid="home-matter"
      >
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
            <div className="md:col-span-4">
              <p className="font-display text-[1.35rem] md:text-[1.5rem] text-[hsl(var(--md-ink))] leading-snug max-w-[24ch]">
                {home.matterLine1}
              </p>
            </div>
            <div className="md:col-span-8">
              <p className="editorial-muted max-w-[62ch]">
                {home.matterLine2}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PIÈCES CHOISIES ----------------------------------------------- */}
      <section className="py-24 md:py-36 border-t border-[hsl(var(--md-line))]" data-testid="home-featured">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between mb-16 md:mb-24">
            <p className="font-eyebrow text-[hsl(var(--md-stone))]">{home.featuredEyebrow}</p>
            <div className="hidden md:block hairline flex-1 mx-12" />
            <Link
              to="/collection"
              className="font-eyebrow text-[hsl(var(--md-ink))] hover:text-[hsl(var(--md-stone))] transition-colors flex items-center gap-3"
              data-testid="home-link-featured-all"
            >
              {home.featuredAll}
              <ArrowRight size={14} strokeWidth={1.2} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8 lg:gap-14">
            {featured.map((p, i) => (
              <Link
                to={`/collection/${p.slug}`}
                key={p.slug}
                data-testid={`featured-${p.slug}`}
                className={`group block ${i === 1 ? "md:translate-y-20" : ""}`}
              >
                <div className="relative aspect-[4/5] bg-[hsl(var(--md-bone))] border border-[hsl(var(--md-line))] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]"
                    width="1150"
                    height="1150"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="absolute top-5 left-5 font-eyebrow text-[hsl(var(--md-stone))]">
                    N°&nbsp;{String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="mt-8">
                  <p className="font-eyebrow text-[hsl(var(--md-stone))]">
                    {local(p.capacityLabel, lang)}
                  </p>
                  <h3 className="font-display text-[1.95rem] mt-3 text-[hsl(var(--md-ink))] leading-tight">
                    {p.name}
                  </h3>
                  <p className="font-display italic text-[1.1rem] mt-2 text-[hsl(var(--md-stone))] leading-snug">
                    {local(p.tagline, lang)}
                  </p>

                  <div className="hairline mt-7" />

                  <div className="mt-5 flex items-baseline justify-between">
                    <span className="font-display text-[1.4rem] text-[hsl(var(--md-ink))]">
                      {local(p.priceLabel, lang)}
                    </span>
                    <span className="font-eyebrow text-[hsl(var(--md-ink))] flex items-center gap-2 group-hover:gap-3 transition-all">
                      {home.featuredCta}
                      <ArrowRight size={13} strokeWidth={1.2} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DEUX VOIES ---------------------------------------------------- */}
      <section
        className="bg-[hsl(var(--md-bone))] border-y border-[hsl(var(--md-line))] py-24 md:py-36 relative overflow-hidden"
        data-testid="home-paths"
      >
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="font-eyebrow text-[hsl(var(--md-stone))] mb-14 md:mb-20">
            {home.pathsEyebrow}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 lg:gap-28">
            {/* Voie 1 — Les Pièces */}
            <div className="flex flex-col" data-testid="path-pieces">
              <div className="hairline mb-8" />
              <h2 className="font-display text-[2rem] md:text-[2.4rem] leading-tight text-[hsl(var(--md-ink))]">
                {home.pathPiecesTitle}
              </h2>
              <p className="editorial mt-6 max-w-[42ch] text-[hsl(var(--md-ink))]">
                {home.pathPiecesBody}
              </p>
              <div className="mt-auto pt-10">
                <Link
                  to="/collection"
                  className="btn-ghost inline-flex"
                  data-testid="path-pieces-cta"
                >
                  {home.pathPiecesCta}
                  <ArrowUpRight size={14} strokeWidth={1.25} />
                </Link>
              </div>
            </div>

            {/* Voie 2 — Études Privées */}
            <div className="flex flex-col md:mt-16" data-testid="path-etudes">
              <div className="hairline-ember mb-8" />
              <h2 className="font-display text-[2rem] md:text-[2.4rem] leading-tight text-[hsl(var(--md-ink))]">
                {home.pathEtudesTitle}
              </h2>
              <p className="editorial mt-6 max-w-[42ch] text-[hsl(var(--md-ink))]">
                {home.pathEtudesBody}
              </p>
              <div className="mt-auto pt-10">
                <Link
                  to="/univers#etudes-privees"
                  className="btn-ghost inline-flex"
                  data-testid="path-etudes-cta"
                >
                  {home.pathEtudesCta}
                  <ArrowUpRight size={14} strokeWidth={1.25} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PrivatePresentationCTA />
    </div>
  );
};

export default Home;
